import {
    AssertionDescriptorUsageTypesEnum,
    CaseFileStaticItems,
    QueryToolRedirectTypeEnum,
    LookupDataEnums,
} from '../../utilities/Constants';

import { IQueryFilterCriteria } from '../../interfaces/IQuery';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { Query } from './Query';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCaseManagersInZone } from '../../shared/react-query-hooks/useCaseManagersInZone';
import { useCaseStatusTypesLookup } from '../../shared/react-query-hooks/useCaseStatusTypesLookup';
import { useDefenseCounselsLookup } from '../../shared/react-query-hooks/useDefenseCounselsLookup';
import { useGetAcknowledgmentStatusTypes } from '../../shared/react-query-hooks/useGetAcknowledgmentStatusTypes';
import { useGetApprovalStatusTypes } from '../../shared/react-query-hooks/useGetApprovalStatusTypes';
import { useGetDepositionCategories } from '../../shared/react-query-hooks/useGetDepositionCategories';
import { useGetDepositionStatusTypes } from '../../shared/react-query-hooks/useGetDepositionStatusTypes';
import {useGetNoteReviewStatusTypes} from '../../shared/react-query-hooks/useGetNoteReviewStatusTypes';
import { useGetNotePurposeTypes } from '../../shared/react-query-hooks/useGetNotePurposeTypes';
import { useGetPackageDeals } from '../../shared/react-query-hooks/useGetPackageDeals';
import { useGetUsStatesLookup } from '../../shared/react-query-hooks/useUsStatesLookup';
import { useLocation } from 'react-router-dom';
import { useNoteCategoryTypes } from '../../shared/react-query-hooks/useNoteCategoryTypes';
import { useZoneContext } from '../../contexts/useZoneContext';
import { useZoneGetAssertionDescriptors } from '../../shared/react-query-hooks/useZoneGetAssertionDescriptors';
import { useZoneGetStaticItems } from '../../shared/react-query-hooks/useZoneGetStaticItems';
import { useZoneUsers } from '../../shared/react-query-hooks/useZoneUsers';
import { useGetLookupData } from '../../shared/react-query-hooks/useGetLookupData';
import { useGetPlaintiffsFirms } from '../../shared/react-query-hooks/useGetPlaintiffsFirms';
export interface IQueryWrapperProps {
    displayColumnsSelected?: IQueryFilterCriteria[];
    queryFilterCriteriaSelected?: IQueryFilterCriteria[];
    redirectType?: QueryToolRedirectTypeEnum;
    title?: string;
    redirectUrl?: string;
    redirectComponentCurrentState?: string;
    refreshIdentifier?: string;
}

export function QueryWrapper(props: IQueryWrapperProps) {
    const { state: locationState } = useLocation();
    const { user } = useAuthContext();
    const zoneConext = useZoneContext();
    const acknowledgmentStatusTypes = useGetAcknowledgmentStatusTypes();
    const approvalStatusTypes = useGetApprovalStatusTypes();
    const assertionDescriptorsLookup = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Case
    );
    const caseManagersLookup = useCaseManagersInZone();
    const caseStatusTypesLookup = useCaseStatusTypesLookup();
    const defenseCounselsLookup = useDefenseCounselsLookup();
    const depositionCategories = useGetDepositionCategories();
    const depositionStatusTypes = useGetDepositionStatusTypes();
    const noteCategoryTypes = useNoteCategoryTypes();
    const noteReviewStatusTypes = useGetNoteReviewStatusTypes();
    const notePurposes = useGetNotePurposeTypes();
    const staticItems = useZoneGetStaticItems();
    const usStatesLookup = useGetUsStatesLookup();
    const zoneUsersLookup = useZoneUsers(zoneConext.zoneApiClient);
    const packageDeals = useGetPackageDeals(
        !!(staticItems.data ?? []).find((si) => si.id === CaseFileStaticItems.PackageSettlement.Id)
    );
    const packageDealStatuses = useGetLookupData(LookupDataEnums.PackageSettlementStatusTypes);
    const plaintiffsFirms = useGetPlaintiffsFirms();
    const isLoadingLookups =
        acknowledgmentStatusTypes.isLoading ||
        approvalStatusTypes.isLoading ||
        assertionDescriptorsLookup.isLoading ||
        caseManagersLookup.isLoading ||
        caseStatusTypesLookup.isLoading ||
        defenseCounselsLookup.isLoading ||
        depositionCategories.isLoading ||
        depositionStatusTypes.isLoading ||
        noteReviewStatusTypes.isLoading ||
        noteCategoryTypes.isLoading ||
        notePurposes.isLoading ||
        staticItems.isLoading ||
        usStatesLookup.isLoading ||
        zoneUsersLookup.isLoading ||
        packageDeals.isLoading||
        packageDealStatuses.isLoading ||
        plaintiffsFirms.isLoading;

    const hasLookupError =
        approvalStatusTypes.isError ||
        acknowledgmentStatusTypes.isError ||
        assertionDescriptorsLookup.isError ||
        caseManagersLookup.isError ||
        caseStatusTypesLookup.isError ||
        defenseCounselsLookup.isError ||
        depositionCategories.isError ||
        depositionStatusTypes.isError ||
        noteReviewStatusTypes.isError ||
        noteCategoryTypes.isError ||
        notePurposes.isError ||
        staticItems.isError ||
        usStatesLookup.isError ||
        zoneUsersLookup.isError ||
        packageDeals.isError||
        packageDealStatuses.isError ||
        plaintiffsFirms.isError;

    let validation: IValidation = {};

    if (hasLookupError) {
        validation = {
            ...validation,
            lookups: [
                'An unhandled error occurred while retrieving Query Criteria data. Please refresh your page and try again. If the issue persists, contact your administrator',
            ],
        };
    }

    if (isLoadingLookups) {
        return <Loader />;
    }

    return (
        <Query
            acknowledgmentStatusTypes={acknowledgmentStatusTypes?.data ?? []}
            approvalStatusTypes={approvalStatusTypes?.data ?? []}
            assertionDescriptors={assertionDescriptorsLookup?.data ?? []}
            caseManagers={caseManagersLookup?.data ?? []}
            caseStatuses={caseStatusTypesLookup?.data ?? []}
            defenseCounsels={defenseCounselsLookup?.data ?? []}
            depositionCategories={depositionCategories?.data ?? []}
            depositionStatusTypes={depositionStatusTypes?.data ?? []}
            noteReviewStatusTypes={noteReviewStatusTypes?.data ?? []}
            noteCategoryTypes={noteCategoryTypes?.data ?? []}
            notePurposeTypes={notePurposes?.data ?? []}
            packageDeals={packageDeals?.data ?? []}
            redirectModel={locationState?.redirectModel}
            staticItems={staticItems.data ?? []}
            user={user}
            usStates={usStatesLookup.data ?? []}
            validation={validation}
            zoneUsers={zoneUsersLookup.data ?? []}
            zoneContext={zoneConext}
            isQuadQuery={false}
            packageDealStatuses={packageDealStatuses?.data ?? []}
            plaintiffsFirms={plaintiffsFirms?.data ?? []}
        />
    );
}

export interface IQueryRedirectModel {
    queryFilterCriteriaSelected: IQueryFilterCriteria[];
    displayColumnsSelected: IQueryFilterCriteria[];
    title: string;
    redirectUrl: string;
    redirectType: QueryToolRedirectTypeEnum | undefined;
    redirectComponentCurrentState: any;
    showSelectAllCheckbox?: boolean;
}
